/**
 * Black Friday Sale Event Landing Page Configuration
 * 
 * Generated by: Landing Page Generator CLI
 * Page Type: event
 * Category: event
 */

import type { LandingPageConfig } from './types';
import { baseConfig } from './base.config';

export const blackFridayConfig: LandingPageConfig = {
  ...baseConfig,
  
  // Metadata
  id: 'black-friday',
  name: 'Black Friday Sale',
  slug: 'black-friday',
  category: 'event',
  pageType: 'event',
  seo: {
    title: 'Limited Time: Black Friday Pillow Sale – FluffCo',
    description: 'Save big on premium pillows this Black Friday. Hotel-quality comfort at unbeatable prices.',
    keywords: ['black friday', 'pillow sale', 'black friday deals', 'premium pillows', 'hotel quality pillows'],
  },
  
  // Global settings - Black Friday themed
  theme: 'dark',
  primaryColor: '#000000', // Black
  accentColor: '#FFD700',  // Gold
  
  // Section visibility and order - Sale-focused flow
  sections: [
    'hero',
    'benefits',
    'sleeper-types',
    'the-difference',
    'sleep-experts',
    'technology',
    'fluffco-secret',
    'features-grid',
    'testimonials',
    'pricing',
    'faq',
  ],
  
  // Announcement Bar - Black Friday themed
  announcementBar: {
    text: 'Black Friday: Up to 66% Off Premium Pillows',
    showTimer: true,
    ctaText: 'Shop Black Friday →',
    ctaTarget: 'pricing',
  },
  
  // Header
  header: {
    navItems: [
      { id: 'benefits', label: 'Benefits' },
      { id: 'the-difference', label: 'The Difference' },
      { id: 'technology', label: 'Technology' },
      { id: 'testimonials', label: 'Reviews' },
      { id: 'faq', label: 'FAQ' },
    ],
    ctaText: 'Claim Now →',
  },
  
  // Hero Section - Black Friday messaging
  hero: {
    headline: 'The Pillow That Holds Its Shape. Night After Night.',
    subheadline: 'Engineered for consistent support that doesn\'t sag, flatten, or require constant adjustment. Built for sleepers who value reliability over novelty.',
    eventHeadline: 'Black Friday Sale.\nPremium Comfort.\nUnbeatable Prices.',
    trustBadge: {
      reviewCount: '546,000+',
      rating: '95/100',
      source: 'PureWow',
    },
    usps: [
      { icon: 'clock', title: 'Limited Time Offer', description: 'Up to 66% off' },
      { icon: 'shield', title: 'Award-Winning Quality', description: 'Oprah Daily recognized' },
      { icon: 'clock', title: 'Premium Comfort', description: 'Hotel-quality sleep' },
      { icon: 'location', title: 'Made in USA', description: 'Premium materials' },
    ],
    showOekoTexBadge: true,
    showOprahBadge: true,
    show30NightsBadge: true,
  },
  
  // Gallery - Black Friday images (reusing legacy images + 1 new packaging image)
  gallery: {
    images: [
      { src: 'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/mqJDnFMoaMnTVstI.png', alt: 'FluffCo Pillow - Black Friday Sale', type: 'image' },
      { src: 'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/OOtRBgHLZECUSwVP.png', alt: 'Premium Quality', type: 'image', badges: [{ type: 'text', position: 'bottom-left', content: '3 tips' }] },
      { src: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/engineering-detail-new_3ea51b01.png', alt: '3-Layer Construction', type: 'image', badges: [{ type: 'text', position: 'bottom-left', content: '3 layers' }] },
      { src: '', alt: 'Comparison', type: 'comparison' },
      { src: '', alt: 'Hotel Comparison', type: 'hotel-comparison' },
      { src: 'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/RVxCLjqishtuSISc.png', alt: 'Easy Care', type: 'image', badges: [{ type: 'text', position: 'bottom-left', content: 'care' }] },
      { src: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_06f2e2f5.png', alt: 'Black Friday Packaging', type: 'image', badges: [{ type: 'text', position: 'bottom-left', content: 'hotel' }] },
  ],// Black Friday sale-focused overlay text
    slotOverrides: {
      tips: {
        pills: [
          { number: 1, title: 'Save Big', description: 'Up to 66% off premium pillows' },
          { number: 2, title: 'Premium Quality', description: 'Hotel-grade comfort at sale prices' },
          { number: 3, title: 'Limited Time', description: 'Black Friday pricing ends soon' },
        ],
      },
      layers: {
        pills: [
          { number: 1, title: 'Support Layer', description: 'Firm foundation' },
          { number: 2, title: 'Comfort Core', description: 'Plush microfiber' },
          { number: 3, title: 'Cooling Shell', description: 'Breathable cotton' },
        ],
      },
      care: {
        pills: [
          { number: 1, title: 'Machine Wash', description: 'Easy cleaning' },
          { number: 2, title: 'Tumble Dry', description: 'Quick drying' },
          { number: 3, title: 'No Ironing', description: 'Ready to use' },
        ],
      },
      summary: {
        headline: 'Premium Comfort, Sale Prices',
      },
    },
    showAwardCarousel: true,
    awardCarouselItems: [
      { title: 'Sleep O-Wards 2024', source: 'Oprah Daily' },
      { title: 'Best Overall Pillow', source: 'Apartment Therapy' },
      { title: 'Best Down Pillow', source: 'Men\'s Health' },
      { title: 'Editor\'s Pick', source: 'Healthline' },
      { title: '95/100 Rating', source: 'PureWow' },
    ],
  },
  
  // Benefits Section - Sale-focused messaging
  benefits: {
    sectionTitle: 'End Your Pillow Battle',
    sectionSubtitle: 'Fighting with overheating or a sore neck and tight shoulders? Experience the comfort of being coolly cradled and finding the \'Sweet Spot\' every night.',
    benefits: [
      { icon: 'moon', title: 'Sleep like Royalty', description: 'Frustrated with overpriced pillows? FluffCo pillows are the same quality of luxury hotel pillows. For durable, luxurious comfort at an affordable price.' },
      { icon: 'sparkles', title: 'Wake Up Pain-Free', description: 'Your neck rests in its natural position all night. Morning stiffness becomes a thing of the past.' },
      { icon: 'refresh', title: 'Built for Real Life', description: 'Machine washable, maintains shape after cleaning. Practical hygiene without compromising support.' },
    ],
  },
  
  // Sleeper Types Section
  sleeperTypes: {
    sectionTitle: 'Perfect for Every Sleeper',
    sectionSubtitle: 'No matter how you sleep, you\'ll love it',
    sleeperTypes: [
      { type: 'back', image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/sleep-position-back_c9753bdc.png', title: 'Back', description: 'Cradles neck curve' },
      { type: 'side', image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/sleep-position-side_86d85ca5.png', title: 'Side', description: 'Fills shoulder gap' },
      { type: 'stomach', image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/sleep-position-stomach_ac1d0f29.png', title: 'Stomach', description: 'Low profile support' },
    ],
    trustBadges: [
      { icon: 'heart', text: 'Sale Pricing' },
      { icon: 'moon', text: 'Limited Time' },
    ],
  },
  
  // Price Comparison - Enabled for Black Friday (show savings)
  priceComparison: {
    ...baseConfig.priceComparison,
    enabled: true,
  },
  
  // The Difference Section - Compare cheap vs. premium pillows
  difference: {
    standardPillowTitle: 'Cheap Pillows Fall Short',
    standardPillowPoints: [
      'Flatten within weeks',
      'Require constant fluffing',
      'Lose shape after washing',
      'Cause neck pain and stiffness',
      'Need frequent replacement',
    ],
    fluffcoPillowTitle: 'FluffCo: Premium Quality That Lasts',
    fluffcoPillowPoints: [
      'Holds shape for years',
      'No daily fluffing needed',
      'Machine washable durability',
      'Proper neck support all night',
      'One-time investment in comfort',
    ],
  },
  
  // Sleep Experts Section - Sale value messaging
  sleepExperts: {
    sectionTitle: 'The Pillow Sleep Experts Recommend',
    sectionSubtitle: 'Luxury Comfort, Zero Guilt (or Allergies!)',
    cards: [
      { image: '/images/expert-couple.png', title: 'Perfect for Couples', expandedContent: ['Upgrade your sleep together', 'Consistent support for all positions', 'Temperature-neutral comfort'] },
      { image: '/images/expert-gift.png', title: 'Premium Quality', expandedContent: ['Hotel-grade construction', 'Sanforized fabric process', 'Built to last for years'] },
      { image: '/images/expert-quality.png', title: 'Award-Winning', expandedContent: ['Oprah Daily recognized', 'Apartment Therapy Best Overall', '95/100 PureWow rating'] },
      { image: '/images/expert-everyday.png', title: 'Everyday Luxury', expandedContent: ['Used and appreciated nightly', 'Maintains comfort over time', 'Smart investment in sleep'] },
    ],
  },
  
  // Technology Section
  technology: {
    sectionTitle: 'The Quality Behind the Savings',
    features: [
      { id: 'sanforized', title: 'Sanforized Construction', description: 'A specialized process used by luxury hotel suppliers. Pre-shrinks and stabilizes the fabric, preventing the compression and shape loss that plagues standard pillows.', tags: ['Holds Shape', 'More Durable', 'Easy to Clean', 'Crisp Texture'] },
      { id: 'microfiber', title: 'Microfiber Fill', description: 'Fibers 5x thinner than human hair create plush, down-like support without the allergens. Maintains loft while providing consistent resistance.', tags: ['Hypoallergenic', 'High Loft', 'Temperature Neutral', 'Wear Resistant'] },
    ],
  },
  
  // FluffCo Secret Section
  fluffcoSecret: {
    features: [
      { title: 'Why It\'s Worth the Investment', description: '"Sanforizing" is a special and rare way to make pillows that only the 5-star suppliers do. This unique method of craftsmanship allows these pillows to hold their shape night in and night out - as luxury pillows should!', bulletPoints: ['Holds Their Shape', 'More Durable', 'Easy to Clean', 'Extra Crisp Texture', 'Softer to the Touch'] },
      { title: 'Luxury You Can Feel', description: 'The best pillows are made with the thinnest threads. Hotel-pillows use super thin fibers to make the inside of the pillow feel squishy but also firm at the same time.', bulletPoints: ['Plush, Down-Like Softness', 'Higher Loft (height and fluffiness)', 'Cooler Temperatures', 'Resistant to Wear and Tear', 'Hypoallergenic'] },
    ],
  },
  
  // Features Grid Section
  featuresGrid: {
    sectionTitle: 'Premium Quality That Lasts',
    features: [
      { icon: 'clock', title: 'Holds Shape for Years', description: 'Sanforized construction maintains loft and structure. No flattening, no sagging.' },
      { icon: 'refresh', title: 'No Daily Fluffing', description: 'The structural core holds its position. Make your bed once and forget about it.' },
      { icon: 'droplets', title: 'Machine Washable', description: 'Reinforced seams handle regular cleaning. Practical hygiene without compromise.' },
      { icon: 'shield', title: 'Hypoallergenic', description: 'Down alternative fill provides luxury feel without allergens or ethical concerns.' },
      { icon: 'thermometer', title: 'Temperature Neutral', description: 'Breathable cotton cover stays comfortable. No overheating, no cold spots.' },
      { icon: 'layers', title: 'Consistent Support', description: 'Same feel on night 1,000 as night 1. Your neck knows what to expect.' },
    ],
  },
  
  // Testimonials Section - Sale-focused reviews
  testimonials: {
    sectionTitle: 'Hotel-Level Comfort',
    sectionSubtitle: 'Join the community who finally found their pillow',
    showMoreButton: true,
    testimonials: [
      { name: 'Sarah M.', location: 'New York', verified: true, content: 'I waited for Black Friday to buy these and I\'m so glad I did. The quality is incredible for the price!', highlight: 'Incredible quality for the price' },
      { name: 'Michael R.', location: 'California', verified: true, content: 'Best Black Friday purchase I\'ve ever made. These pillows are worth every penny, even at full price.', highlight: 'Worth every penny' },
      { name: 'Jennifer L.', location: 'Texas', verified: true, content: 'I bought 4 pillows during the sale and replaced every pillow in the house. Game changer!', highlight: 'Game changer' },
      { name: 'David K.', location: 'Florida', verified: true, content: 'The sale pricing made it easy to upgrade. Now I understand why hotels use these.', highlight: 'Hotel-quality comfort' },
      { name: 'Lisa P.', location: 'Illinois', verified: true, content: 'I\'ve been sleeping on mine for 2 years and it still feels brand new. Best investment in my sleep.', highlight: 'Best investment in my sleep' },
      { name: 'Robert T.', location: 'Washington', verified: true, content: 'Skeptical at first, but after one night I ordered 3 more. The Black Friday deal was just a bonus.', highlight: 'Ordered 3 more' },
    ],
  },
  
  // Pricing Section
  pricing: {
    ...baseConfig.pricing,
    sectionTitle: 'Choose Your Setup',
    sectionSubtitle: 'Online-only pricing. Free shipping on all orders.',
  },
  
  // FAQ Section - Sale-focused questions
  faq: {
    ...baseConfig.faq,
    sectionTitle: 'Black Friday Sale Questions',
    items: [
      {
        question: 'How long does the Black Friday sale last?',
        answer: 'Our Black Friday sale runs for a limited time only. Once it ends, prices return to regular retail. We recommend ordering now to lock in the savings.',
      },
      {
        question: 'Is the quality the same as full-price pillows?',
        answer: 'Absolutely! These are the exact same premium pillows we sell year-round. The only difference is the price. Same hotel-quality construction, same materials, same warranty.',
      },
      {
        question: 'Can I return them if I don\'t like them?',
        answer: 'Yes! All orders come with our 30-Night Better Sleep guarantee. If you\'re not sleeping better within 30 nights, return them for a full refund. Sale items are eligible for the same return policy.',
      },
      {
        question: 'How many should I buy?',
        answer: 'Most customers buy 2 or 4 pillows to take advantage of our bundle pricing. Buying 4 pillows gets you 4 free pillowcases and maximizes your savings per pillow.',
      },
      {
        question: 'Do these pillows really last for years?',
        answer: 'Yes! Our Sanforized construction process is the same method used by luxury hotels. Customers report their pillows maintaining shape and support for 3-5+ years with proper care.',
      },
      {
        question: 'What makes these different from cheap pillows?',
        answer: 'The Sanforized fabric process and microfiber fill. Cheap pillows use basic materials that flatten quickly. Our hotel-grade construction maintains loft and support for years, making them a better long-term investment.',
      },
    ],
  },
  
  // Footer
  footer: {
    ...baseConfig.footer,
  },
};
